import React from 'react';
import { AlertTriangle, CheckCircle, XCircle, X } from 'lucide-react';

const ConfirmationModal = ({ 
  isOpen, 
  open, // Support both isOpen and open props
  onClose, 
  onConfirm, 
  title, 
  message, 
  type = 'warning', // 'warning', 'danger', 'success', 'info'
  variant, // Support both type and variant props
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  showCancel = true
}) => {
  // Support both prop names
  const modalOpen = isOpen || open;
  const modalType = type || variant || 'warning';
  
  if (!modalOpen) return null;

  const typeStyles = {
    warning: {
      icon: <AlertTriangle className="w-8 h-8 text-red-600" />,
      bgColor: 'bg-red-100',
      buttonColor: 'bg-red-600 hover:bg-red-700'
    },
    danger: {
      icon: <XCircle className="w-8 h-8 text-red-600" />,
      bgColor: 'bg-red-100',
      buttonColor: 'bg-red-600 hover:bg-red-700'
    },
    success: {
      icon: <CheckCircle className="w-8 h-8 text-green-600" />,
      bgColor: 'bg-green-100',
      buttonColor: 'bg-green-600 hover:bg-green-700'
    },
    info: {
      icon: <AlertTriangle className="w-8 h-8 text-blue-600" />,
      bgColor: 'bg-blue-100',
      buttonColor: 'bg-blue-600 hover:bg-blue-700'
    }
  };

  const style = typeStyles[modalType] || typeStyles.warning;

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on overlay, not on modal content
    if (e.target === e.currentTarget) {
      e.stopPropagation();
      onClose();
    }
  };

  const handleModalClick = (e) => {
    // Prevent clicks inside modal from bubbling to parent dialogs
    e.stopPropagation();
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    onClose();
  };

  const handleConfirm = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onClose();
    onConfirm();
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-scale-in"
        onClick={handleModalClick}
      >
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-full ${style.bgColor}`}>
              {style.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900">{title}</h3>
              <p className="text-gray-600 mt-2 leading-relaxed">{message}</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 rounded-b-xl flex gap-3">
          {showCancel && (
            <button
              onClick={handleCancel}
              className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium text-gray-700"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`flex-1 px-6 py-3 rounded-lg text-white transition-colors font-medium ${style.buttonColor}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
